import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { BounceLoader } from 'react-spinners';
import Button from '../../common/components/Button';
import CopyHtml from '../../common/components/CopyHtml';
import { getCopy } from '../../common/lib/copyDefaults';
import { maskEmail } from '../../common/lib/maskEmail';
import { redirectToLogin, buildRegisterPath } from '../../common/lib/redirectToLogin';
import {
  isClaimedByOtherTab,
  subscribeToRegistrationFlowClaim,
  getRegistrationFlowId,
} from '../../common/lib/registrationFlowSession';
import { getRegistrationProfile } from '../../common/lib/registrationProfileSession';
import { fetchUser, flushUser } from '../../services/auth';
import {
  pollEmailVerification,
  stopPollEmailVerification,
  resendEmailVerification,
} from '../../redux/actions';

import '../../../style/index.css';
import './checkEmail.css';

const mapStateToProps = (state) => ({
  isResendingEmail: state.registerReducer.isResendingEmail,
  resendEmailMessage: state.registerReducer.resendEmailMessage,
  resendEmailMessageIsError: state.registerReducer.resendEmailMessageIsError,
  resendCooldownRemaining: state.registerReducer.resendCooldownRemaining,
});

const mapDispatchToProps = {
  pollEmailVerification,
  stopPollEmailVerification,
  resendEmailVerification,
};

class CheckEmailView extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    pollEmailVerification: PropTypes.func.isRequired,
    stopPollEmailVerification: PropTypes.func.isRequired,
    resendEmailVerification: PropTypes.func.isRequired,
    isResendingEmail: PropTypes.bool.isRequired,
    resendEmailMessage: PropTypes.string.isRequired,
    resendEmailMessageIsError: PropTypes.bool.isRequired,
    resendCooldownRemaining: PropTypes.number.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = { showReturnSection: false };
    this.handleResendClick = this.handleResendClick.bind(this);
    this.handleReturnToRegistration = this.handleReturnToRegistration.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleRegistrationFlowClaim = this.handleRegistrationFlowClaim.bind(this);
    this.startPolling = this.startPolling.bind(this);
    this.redirectToLogin = this.redirectToLogin.bind(this);
    this.unsubscribeFromRegistrationFlowClaim = null;
  }

  redirectToLogin() {
    const { history, stopPollEmailVerification } = this.props;
    stopPollEmailVerification();
    redirectToLogin(history);
  }

  handleRegistrationFlowClaim() {
    if (isClaimedByOtherTab()) {
      this.redirectToLogin();
    }
  }

  startPolling() {
    if (isClaimedByOtherTab()) {
      this.redirectToLogin();
      return;
    }
    const {
      pollEmailVerification, stopPollEmailVerification, history, 
    } = this.props;
    stopPollEmailVerification();
    pollEmailVerification(history.push);
  }

  componentDidMount() {
    this.unsubscribeFromRegistrationFlowClaim = subscribeToRegistrationFlowClaim(
      this.handleRegistrationFlowClaim,
    );
    if (isClaimedByOtherTab()) {
      this.redirectToLogin();
      return;
    }
    if (document.visibilityState === 'visible') {
      this.startPolling();
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    requestAnimationFrame(() => {
      const el = document.getElementById('check-email-heading');
      if (el) {
        el.focus();
      }
    });
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.unsubscribeFromRegistrationFlowClaim) {
      this.unsubscribeFromRegistrationFlowClaim();
    }
    this.props.stopPollEmailVerification();
  }

  componentDidUpdate(prevProps) {
    if (
      !prevProps.resendEmailMessage &&
      this.props.resendEmailMessage &&
      !this.props.resendEmailMessageIsError
    ) {
      this.setState({ showReturnSection: true });
    }
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      if (isClaimedByOtherTab()) {
        this.redirectToLogin();
        return;
      }
      this.startPolling();
    } else {
      this.props.stopPollEmailVerification();
    }
  }

  handleResendClick(e) {
    e.preventDefault();
    const {
      isResendingEmail, resendCooldownRemaining, resendEmailVerification, history, 
    } =
      this.props;
    if (isResendingEmail || resendCooldownRemaining > 0) {
      return;
    }
    resendEmailVerification(history.push);
  }

  handleReturnToRegistration(e) {
    e.preventDefault();
    const { history, stopPollEmailVerification } = this.props;
    const registerPath = buildRegisterPath();
    stopPollEmailVerification();
    flushUser();
    history.push(registerPath);
  }

  render() {
    const copy = getCopy('/check-email');
    const user = fetchUser();
    const emailFromToken = user.identity && user.identity.email;
    const {
      isResendingEmail,
      resendEmailMessage,
      resendEmailMessageIsError,
      resendCooldownRemaining,
    } = this.props;
    const resendDisabled = isResendingEmail || resendCooldownRemaining > 0;
    const cooldownMessage =
      resendCooldownRemaining > 0
        ? copy.resendCooldown.replace('{seconds}', String(resendCooldownRemaining))
        : '';
    const isRegistrationFlow = Boolean(getRegistrationFlowId() || getRegistrationProfile());
    const { showReturnSection } = this.state;

    return (
      <div className="check-email row justify-content-center">
        <div className="col-xl-7 col-lg-8 col-md-8 col-sm-10">
          <div className="check-email__body">
            <h1 id="check-email-heading" tabIndex={-1} className="check-email__title">
              {copy.header}
            </h1>
            <CopyHtml html={copy.body} className="check-email__description" />
            <p className="check-email__email">
              <strong>{emailFromToken}</strong>
            </p>
            <CopyHtml
              html={showReturnSection ? copy.resendHelperPost : copy.resendHelper}
              className="check-email__helper"
            />
          </div>
          <div className="button-wrap">
            {isResendingEmail ? (
              <div className="loading-wrapper">
                <BounceLoader color="#084267" />
              </div>
            ) : (
              <Button
                label={copy.resendButtonLabel}
                onClickFn={this.handleResendClick}
                className={resendDisabled ? 'nyl-button disabled' : 'nyl-button'}
                disabled={resendDisabled}
                id="check-email-resend-button"
              />
            )}
            {(resendEmailMessage || cooldownMessage) && (
              <div
                className="check-email__resend-status"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {resendEmailMessage && (
                  <p
                    className={
                      resendEmailMessageIsError ? 'submit-error' : 'check-email__resend-success'
                    }
                  >
                    {resendEmailMessage}
                  </p>
                )}
                {cooldownMessage && (
                  <p className="check-email__resend-cooldown">{cooldownMessage}</p>
                )}
              </div>
            )}
            {showReturnSection && isRegistrationFlow && (
              <div className="check-email__return-section">
                <CopyHtml html={copy.returnHelper} className="check-email__helper" />
                <Button
                  label={copy.returnButtonLabel}
                  onClickFn={this.handleReturnToRegistration}
                  className="nyl-button nyl-button--secondary"
                  id="check-email-return-button"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CheckEmailView));
